import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { formatDateTime } from '@/utils/date'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

// AI-OFF-SWITCH-2 — on/off screen for AI-OFF-SWITCH-1. Nothing reaches the
// server until Save is pressed; toggling only edits the draft state.

interface RegisteredInfo { name: string; owner: string | null; risk: string | null }

interface ControlsResponse {
  all_off: boolean
  features_off: string[]
  firms_off: string[]
  reason: string
  updated_by: string | null
  updated_at: string | null
  rev: number
  known_features: string[]
  registered_features?: string[]
  registered_info?: Record<string, RegisteredInfo>
  known_firms: string[]
}

type ServerMeta = Pick<
  ControlsResponse,
  'updated_by' | 'updated_at' | 'rev' | 'known_features' | 'registered_features' | 'registered_info' | 'known_firms'
>

const EMPTY_META: ServerMeta = {
  updated_by: null, updated_at: null, rev: 0, known_features: [], registered_features: [], registered_info: undefined, known_firms: [],
}

// The server's 409 texts (stale save / simultaneous save): offer a Reload, never auto-reload or re-send.
const CONFLICT = /changed by someone else|same moment/

function featureLabel(feature: string): string {
  const spaced = (feature.split('.').pop() || feature).replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const RISK_BADGES: Record<string, { label: string; cls: string }> = {
  high: { label: 'High risk', cls: 'bg-red-100 text-red-700' },
  medium: { label: 'Medium risk', cls: 'bg-blue-100 text-blue-700' },
  low: { label: 'Low risk', cls: 'bg-gray-100 text-gray-600' },
}
const NOT_SET = { label: 'Risk not set', cls: 'bg-amber-100 text-amber-700' }
const NOT_REGISTERED = { label: 'Not on register', cls: 'bg-red-100 text-red-700' }

// Display only (AI-REGISTER-3): labels from the Register; they gate nothing.
function FeatureBadges({ feature, info }: { feature: string; info?: RegisteredInfo }) {
  const badge = !info ? NOT_REGISTERED : RISK_BADGES[info.risk ?? ''] ?? NOT_SET
  return (
    <span className="mt-0.5 flex items-center gap-2 text-xs">
      <span data-testid={`feature-risk-${feature}`} className={`rounded px-1.5 py-0.5 ${badge.cls}`}>{badge.label}</span>
      {info && (
        <span data-testid={`feature-owner-${feature}`} className={info.owner ? 'text-gray-600' : 'text-amber-700'}>
          {info.owner ? `Owner: ${info.owner}` : 'No owner'}
        </span>
      )}
    </span>
  )
}

function toggleMember(list: string[], value: string, enabled: boolean): string[] {
  const set = new Set(list)
  enabled ? set.delete(value) : set.add(value)
  return Array.from(set)
}

export function AiControlsSection() {
  const { data, loading, error, execute } = useApi<ControlsResponse>()
  const [allOff, setAllOff] = useState(false)
  const [featuresOff, setFeaturesOff] = useState<string[]>([])
  const [firmsOff, setFirmsOff] = useState<string[]>([])
  const [reason, setReason] = useState('')
  const [confirmAllOff, setConfirmAllOff] = useState(false)
  const [meta, setMeta] = useState<ServerMeta>(EMPTY_META)

  const load = useCallback(
    () => execute(() => apiClient.get<ControlsResponse>('/v1/admin/ai-controls').then(res => res.data)),
    [execute],
  )

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!data) return
    setAllOff(data.all_off)
    setFeaturesOff(data.features_off)
    setFirmsOff(data.firms_off)
    setReason(data.reason)
    setConfirmAllOff(false)
    setMeta({
      updated_by: data.updated_by,
      updated_at: data.updated_at,
      rev: data.rev ?? 0,
      known_features: data.known_features ?? [],
      registered_features: data.registered_features ?? [],
      registered_info: data.registered_info,
      known_firms: data.known_firms ?? [],
    })
  }, [data])

  // Every registered feature, not only those that already ran, so one can be
  // switched off before its first call.
  const ran = new Set(meta.known_features)
  const features = Array.from(new Set([...meta.known_features, ...(meta.registered_features ?? [])])).sort()

  const handleAllToggle = (nextOn: boolean) => {
    if (nextOn) { setAllOff(false); setConfirmAllOff(false) } else { setConfirmAllOff(true) }
  }

  const handleSave = useCallback(async () => {
    const result = await execute(() =>
      apiClient
        .put<ControlsResponse>('/v1/admin/ai-controls', { all_off: allOff, features_off: featuresOff, firms_off: firmsOff, reason, rev: meta.rev })
        .then(res => res.data),
    )
    if (result) await load()
  }, [allOff, featuresOff, firmsOff, reason, meta.rev, execute, load])

  return (
    <section data-testid="ai-controls-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">AI Controls</h2>
      {error && (
        <div className="text-sm text-red-600">
          {error}
          {CONFLICT.test(error) && (
            <Button type="button" variant="outline" size="sm" data-testid="ai-controls-reload" onClick={() => load()} className="ml-3">Reload</Button>
          )}
        </div>
      )}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="rounded-lg border bg-gray-50 px-4 py-3 space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
          <input type="checkbox" data-testid="all-ai-toggle" checked={!allOff} onChange={e => handleAllToggle(e.target.checked)} />
          All AI {allOff ? 'OFF' : 'ON'}
        </label>
        {confirmAllOff && (
          <div data-testid="all-off-confirm" className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm space-y-2">
            <p>Are you sure? This turns AI off for every feature and every firm.</p>
            <div className="flex gap-2">
              <Button type="button" variant="destructive" size="sm" data-testid="all-off-confirm-yes"
                onClick={() => { setAllOff(true); setConfirmAllOff(false) }}>
                Yes, turn off
              </Button>
              <Button type="button" variant="outline" size="sm" data-testid="all-off-confirm-cancel" onClick={() => setConfirmAllOff(false)}>
                Cancel
              </Button>
            </div>
          </div>
        )}
        <Textarea data-testid="all-off-reason" placeholder="Reason (shown in the audit log)" value={reason} onChange={e => setReason(e.target.value)} />
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Features</h3>
        {meta.registered_info && (
          <p data-testid="controls-risk-note" className="text-xs text-gray-500 mb-2">
            Risk and owner come from the AI Register above. They are labels; they do not change what a switch does.
          </p>
        )}
        {features.length === 0 && <p className="text-sm text-gray-500">No AI features have run yet.</p>}
        <div className="space-y-1">
          {features.map(feature => (
            <label key={feature} data-testid={`feature-toggle-${feature}`} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!featuresOff.includes(feature)}
                onChange={e => setFeaturesOff(prev => toggleMember(prev, feature, e.target.checked))} />
              <span>
                {featureLabel(feature)}
                <span className="block text-xs text-gray-400">{feature}</span>
                {meta.registered_info && <FeatureBadges feature={feature} info={meta.registered_info[feature]} />}
                {!ran.has(feature) && <span className="block text-xs text-gray-500">never run</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Firms</h3>
        <p className="text-xs text-gray-500 mb-2">
          Users with no firm name on their account cannot be switched off here — use All or Features.
        </p>
        <div className="space-y-1">
          {meta.known_firms.map(firm => (
            <label key={firm} data-testid={`firm-toggle-${firm}`} className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!firmsOff.includes(firm)}
                onChange={e => setFirmsOff(prev => toggleMember(prev, firm, e.target.checked))} />
              <span>{firm}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <p data-testid="ai-controls-footer" className="text-xs text-gray-500">
          Last changed by {meta.updated_by ?? '—'} at {meta.updated_at ? formatDateTime(meta.updated_at) : '—'}. Changes reach every server within 60 seconds.
        </p>
        <Button type="button" data-testid="ai-controls-save" onClick={handleSave}>Save</Button>
      </div>
    </section>
  )
}
